// Client-side response cache for the API seam (US-883 · R-5/R-6).
//
// Plain module (no React) so it stays testable in isolation. Opt-in only:
// useApi consults it solely when the caller passes options.cache — polling
// (reportStatus) and login must never be cached. Caching POST responses is
// safe here because the six data endpoints are deterministic simulations for
// a given body (the backend caches server-side under the same premise).
//
// Memory-only and per-tab: nothing persists to localStorage; clear() on
// logout (US-882) removes any session residue.

const DEFAULT_TTL_MS = 5 * 60 * 1000;

const entries = new Map(); // key → { value, expiresAt }
const inFlight = new Map(); // key → Promise

// JSON.stringify with object keys sorted at every level, so two semantically
// equal bodies produce the same cache key regardless of key insertion order.
export const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

export const cacheKey = (method, path, body) =>
  `${(method || "GET").toUpperCase()} ${path} ${body === undefined ? "" : stableStringify(body)}`;

// Resolve `key` from the cache within its TTL, dedupe onto the in-flight
// request when one exists, otherwise run `execute` and cache its resolution.
// Failures are never cached — the next call retries the network.
export const withCache = (key, execute, ttl = DEFAULT_TTL_MS) => {
  const entry = entries.get(key);
  if (entry) {
    if (entry.expiresAt > Date.now()) return Promise.resolve(entry.value);
    entries.delete(key);
  }

  if (inFlight.has(key)) return inFlight.get(key);

  const request = Promise.resolve()
    .then(execute)
    .then(
      (value) => {
        // Skip the write when invalidate()/clear() ran mid-flight — a stale
        // request resolving late must not repopulate the cache.
        if (inFlight.get(key) === request) {
          entries.set(key, { value, expiresAt: Date.now() + ttl });
          inFlight.delete(key);
        }
        return value;
      },
      (error) => {
        if (inFlight.get(key) === request) inFlight.delete(key);
        throw error;
      }
    );

  inFlight.set(key, request);
  return request;
};

// Drop every entry (cached or in flight) whose key starts with `prefix`,
// e.g. invalidate("POST /optimal-tech-mix/") after a parameter change (R-6).
export const invalidate = (prefix) => {
  [entries, inFlight].forEach((map) => {
    [...map.keys()]
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => map.delete(key));
  });
};

// Full reset — session transitions (login/logout, US-882) call this so no
// server data survives across users.
export const clear = () => {
  entries.clear();
  inFlight.clear();
};
